import React from 'react';

/**
 * TshirtBlock — collapsible T-shirt configuration sub-section.
 * Mounted inside Basic Info on Show Create/Edit so producers can sell
 * merch alongside pre-entries.
 */
export const TshirtBlock = ({ formData, setFormData, testPrefix = 'edit' }) => (
  <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-4 mt-4" data-testid={`${testPrefix}-tshirt-block`}>
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={!!formData.tshirt_enabled}
        onChange={(e) => setFormData({ ...formData, tshirt_enabled: e.target.checked })}
        className="w-4 h-4 accent-[#FF9500]"
        data-testid={`${testPrefix}-tshirt-enabled`}
      />
      <span className="text-white font-semibold text-sm">Sell Show T-Shirts with Pre-Entries</span>
    </label>
    {formData.tshirt_enabled && (
      <div className="space-y-3 pl-6 mt-3" data-testid={`${testPrefix}-tshirt-fields`}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-[#9E9E9E] mb-1">Label shown to riders</label>
            <input
              type="text"
              value={formData.tshirt_label || ''}
              onChange={(e) => setFormData({ ...formData, tshirt_label: e.target.value })}
              className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
              data-testid={`${testPrefix}-tshirt-label`}
              placeholder="Show T-Shirt"
            />
          </div>
          <div>
            <label className="block text-xs text-[#9E9E9E] mb-1">Price ($)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={formData.tshirt_price || ''}
              onChange={(e) => setFormData({ ...formData, tshirt_price: e.target.value })}
              className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
              data-testid={`${testPrefix}-tshirt-price`}
              placeholder="25"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs text-[#9E9E9E] mb-1">Available sizes (comma-separated)</label>
          <input
            type="text"
            value={(formData.tshirt_sizes || []).join(', ')}
            onChange={(e) =>
              setFormData({
                ...formData,
                tshirt_sizes: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
              })
            }
            className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
            data-testid={`${testPrefix}-tshirt-sizes`}
            placeholder="S, M, L, XL, XXL"
          />
          <p className="text-[10px] text-[#555] mt-1">Riders pick a size on the entry form.</p>
        </div>
      </div>
    )}
  </div>
);

/**
 * TieOutBlock — collapsible tie-out fee sub-section.
 * Mounted inside Stalls/RV on Show Create/Edit (riders without a stall
 * often need an outdoor tie-out spot).
 */
export const TieOutBlock = ({ formData, setFormData, testPrefix = 'edit' }) => (
  <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-4 mt-4 col-span-2" data-testid={`${testPrefix}-tie-out-block`}>
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={!!formData.tie_out_fee_enabled}
        onChange={(e) => setFormData({ ...formData, tie_out_fee_enabled: e.target.checked })}
        className="w-4 h-4 accent-[#FF9500]"
        data-testid={`${testPrefix}-tie-out-enabled`}
      />
      <span className="text-white font-semibold text-sm">Charge a Tie-Out Fee</span>
    </label>
    {formData.tie_out_fee_enabled && (
      <div className="space-y-3 pl-6 mt-3" data-testid={`${testPrefix}-tie-out-fields`}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-[#9E9E9E] mb-1">Label shown to riders</label>
            <input
              type="text"
              value={formData.tie_out_fee_label || ''}
              onChange={(e) => setFormData({ ...formData, tie_out_fee_label: e.target.value })}
              className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
              data-testid={`${testPrefix}-tie-out-label`}
              placeholder="Tie-Out Fee"
            />
          </div>
          <div>
            <label className="block text-xs text-[#9E9E9E] mb-1">Price ($)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={formData.tie_out_fee_price || ''}
              onChange={(e) => setFormData({ ...formData, tie_out_fee_price: e.target.value })}
              className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
              data-testid={`${testPrefix}-tie-out-price`}
              placeholder="20"
            />
          </div>
        </div>
        <p className="text-[10px] text-[#555]">Auto-billed per horse without a stall, per day at the show. (e.g. 2 horses · 0 stalls · 2-day show = 4 × price)</p>
      </div>
    )}
  </div>
);

/**
 * ShavingsBlock — sell shavings/sawdust by the bag.
 * Mounted inside Stalls/RV. Producer sets price per bag; rider picks quantity.
 */
export const ShavingsBlock = ({ formData, setFormData, testPrefix = 'edit' }) => (
  <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-4 mt-4 col-span-2" data-testid={`${testPrefix}-shavings-block`}>
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={!!formData.shavings_enabled}
        onChange={(e) => setFormData({ ...formData, shavings_enabled: e.target.checked })}
        className="w-4 h-4 accent-[#FF9500]"
        data-testid={`${testPrefix}-shavings-enabled`}
      />
      <span className="text-white font-semibold text-sm">Sell Shavings / Sawdust by the Bag</span>
    </label>
    {formData.shavings_enabled && (
      <div className="space-y-3 pl-6 mt-3" data-testid={`${testPrefix}-shavings-fields`}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-[#9E9E9E] mb-1">Label shown to riders</label>
            <input
              type="text"
              value={formData.shavings_label || ''}
              onChange={(e) => setFormData({ ...formData, shavings_label: e.target.value })}
              className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
              data-testid={`${testPrefix}-shavings-label`}
              placeholder="Shavings"
            />
          </div>
          <div>
            <label className="block text-xs text-[#9E9E9E] mb-1">Price per bag ($)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={formData.shavings_price_per_bag || ''}
              onChange={(e) => setFormData({ ...formData, shavings_price_per_bag: e.target.value })}
              className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FF9500]"
              data-testid={`${testPrefix}-shavings-price`}
              placeholder="8"
            />
          </div>
        </div>
        <p className="text-[10px] text-[#555]">Riders enter the number of bags on the entry form. Charged as bags × price.</p>
      </div>
    )}
  </div>
);
